/**
 * 分块存储（DESIGN.md §4.3）。
 *
 * 这是 §2.1「内存与大文档」那条 A 类壁垒的核心答案。一句话概括约束：
 * **文档尺寸不决定内存占用，实际画了多少像素才决定。**
 *
 * 一个 10000×10000 的空白文档若按整图分配是 400 MB，而它应该是 **0 字节**。
 * 做到这点靠三条，缺一不可：
 *
 * 1. **稀疏**：全透明块 `data === null`，根本不分配。
 * 2. **不存在整图 API**。这一条比前两条更重要也更容易被破坏——只要有一个
 *    `getImageData(): Uint8Array` 返回整图，调用方就会用它，稀疏性当场作废。
 *    本文件的公开 API 里没有任何一个函数接受或返回整图缓冲，
 *    test/engine/tile/store.test.ts 用扫描守着这条线。
 * 3. **写入即分配，读取不分配**。读一个从未写过的块返回 `null` 而不是懒分配一块零，
 *    否则"遍历一遍全图"就会把稀疏文档变成稠密的。
 *
 * 写时复制已在 M1-02 接入（见 ./cow）：快照只 retain 不复制，写入共享块时才克隆。
 * LRU 驱逐与 LOD 金字塔分别归 M1-03、M1-04，`state` 字段已为它们留好。
 */
import { TILE, tileKeyString, type LayerId, type LodLevel, type TileKey } from './key';
import {
  ConcurrentWriteError,
  MemoryLedger,
  SharedPixels,
  TileSnapshot,
  WriteGuard,
} from './cow';

/** 每块的字节数。RGBA8 固定 4 通道 8 位。 */
export const BYTES_PER_TILE = TILE * TILE * 4;

/**
 * 块的驻留状态（§4.3）。
 *
 * `evicted` 与 `loading` 由 M1-03 的驱逐/回载使用，这里先定义好：
 * 状态机后加字段会让所有 switch 都成为待修改点。
 */
export type TileState = 'resident' | 'evicted' | 'loading';

/**
 * 一块像素。
 *
 * `data === null` 表示**全透明**，不占内存——这是稀疏性的全部含义。
 * 注意它与"块不存在"是同一件事：存储里查不到的块也视为全透明，
 * 两者不作区分，否则调用方要处理两种"空"。
 */
export interface Tile {
  readonly key: TileKey;
  /** 全透明时为 null。非空时长度恒为 {@link BYTES_PER_TILE}。 */
  readonly data: Uint8Array | null;
  /** 写时复制引用计数。当前状态算一个，每个引用它的快照各算一个。 */
  readonly refCount: number;
  readonly state: TileState;
  readonly dirty: boolean;
}

/**
 * 图层持有的句柄。
 *
 * **图层不持有像素**，只持有这个句柄（CLAUDE.md 规则 6）。类型层面就不可能
 * 从图层拿到 `ArrayBuffer`：句柄上没有任何暴露缓冲的成员。
 * 要读像素必须经 TileStore，那里才有引用计数与驱逐状态的完整视图。
 */
export interface TileHandle {
  readonly layer: LayerId;
  /** 已分配（非全透明）的块数。 */
  readonly residentTiles: number;
  /** 已分配字节数。空白图层恒为 0。 */
  readonly allocatedBytes: number;
}

/** 内部可变结构 → 对外只读视图。 */
function toTile(tile: MutableTile): Tile {
  return {
    key: tile.key,
    data: tile.pixels?.bytes ?? null,
    refCount: tile.pixels?.refCount ?? 0,
    state: tile.state,
    dirty: tile.dirty,
  };
}

/** 分配统计，用于测试断言与内存面板。 */
export interface StoreStats {
  /** 已分配（非全透明）的块数。 */
  readonly residentTiles: number;
  /** 登记在册的块数，含全透明块。 */
  readonly trackedTiles: number;
  readonly allocatedBytes: number;
  readonly layers: number;
}

interface MutableTile {
  key: TileKey;
  /** 全透明时为 null。多个快照可共享同一个 SharedPixels 实例。 */
  pixels: SharedPixels | null;
  state: TileState;
  dirty: boolean;
}

/**
 * 分块存储。
 *
 * 线程模型：本类只在**拥有它的那个线程**里被访问。跨 Worker 共享像素靠
 * `SharedArrayBuffer`（M1-02 之后接入），而不是共享这个对象本身。
 */
export class TileStore {
  readonly #tiles = new Map<string, MutableTile>();
  /** 每个图层的键集合，用于按图层统计与整层丢弃。 */
  readonly #byLayer = new Map<LayerId, Set<string>>();
  /** 持有未释放写入许可的块。见 beginWrite。 */
  readonly #writing = new Set<string>();
  /**
   * 内存账本。所有分配统计的**唯一**来源。
   *
   * 不自己维护计数器：快照释放像素时 store 无从知晓，两处记账必然分叉，
   * 表现为"释放了快照但内存没降"。共用一本账则谁释放都自动反映。
   */
  readonly #ledger = new MemoryLedger();

  /**
   * 读取一块。
   *
   * 从未写过的块返回 `null`（全透明），**不会**因此分配任何内存——
   * 这正是"遍历全图不会让稀疏文档变稠密"的保证所在。
   */
  get(key: TileKey): Tile | null {
    const tile = this.#tiles.get(tileKeyString(key));
    if (tile === undefined) return null;
    return toTile(tile);
  }

  /** 该块是否已分配像素。全透明与不存在都返回 false。 */
  isResident(key: TileKey): boolean {
    // 不能写成 `?.pixels !== null`：块不存在时可选链得到 undefined，
    // 而 undefined !== null 为真——会把从未写过的块报成已驻留，
    // 于是"空白文档 0 字节"这条不变量在读取侧被悄悄破坏。
    const tile = this.#tiles.get(tileKeyString(key));
    return tile !== undefined && tile.pixels !== null;
  }

  /**
   * 写入一块像素。
   *
   * @param pixels 长度必须恰为 {@link BYTES_PER_TILE}。**会被复制**——
   *   存储必须拥有自己的副本，否则调用方随后修改自己的缓冲会让存储内容
   *   在无人调用写接口的情况下发生变化，这种 bug 几乎无法追踪。
   */
  set(key: TileKey, pixels: Uint8Array): void {
    if (pixels.length !== BYTES_PER_TILE) {
      throw new RangeError(
        `块数据长度必须是 ${BYTES_PER_TILE}（${TILE}×${TILE}×4），收到 ${pixels.length}。`,
      );
    }
    this.#store(key, SharedPixels.from(pixels, this.#ledger));
  }

  /**
   * 分配一块全零（透明）像素并返回可写视图。
   *
   * 与 {@link set} 的区别是省掉一次复制，供解码器等"就地填充"的场景使用。
   * **注意这会立即分配 256 KB**——只在确实要写入时调用，不要用它来"确保块存在"。
   */
  allocate(key: TileKey): Uint8Array {
    const existing = this.#tiles.get(tileKeyString(key));
    if (existing?.pixels != null) {
      // 已被快照共享时必须先克隆，否则就地写入会改到快照看到的内容
      if (existing.pixels.shared) this.#detach(existing);
      return existing.pixels.bytes;
    }
    const pixels = SharedPixels.zeroed(this.#ledger);
    this.#store(key, pixels);
    return pixels.bytes;
  }

  #store(key: TileKey, pixels: SharedPixels): void {
    const id = tileKeyString(key);
    const existing = this.#tiles.get(id);

    if (existing === undefined) {
      this.#tiles.set(id, { key, pixels, state: 'resident', dirty: true });
      this.#indexLayer(key.layer, id);
      return;
    }

    // 新数据的分配已由 SharedPixels 构造时记账；这里只需放手旧数据。
    // 旧数据被快照引用时 release 返回 false，内存留在快照手里，账本自会反映。
    existing.pixels?.release();
    existing.pixels = pixels;
    existing.state = 'resident';
    existing.dirty = true;
  }

  /**
   * 把一个被共享的块换成独占副本（写时复制的"复制"那一步）。
   *
   * 克隆会真的多占一块内存——这是必需的开销，不是浪费：
   * 快照与当前状态从此各持一份，谁也改不到谁。
   */
  #detach(tile: MutableTile): void {
    if (tile.pixels === null || !tile.pixels.shared) return;
    const copy = tile.pixels.clone(); // 克隆时已记账
    tile.pixels.release();
    tile.pixels = copy;
  }

  /**
   * 把一块标记为全透明并释放其内存。
   *
   * 保留登记项而不是直接删除：`dirty` 需要继续传播给合成器——
   * 一块从有内容变成全透明，画面是要重绘的。真正的删除走 {@link dropLayer}。
   */
  clear(key: TileKey): void {
    const id = tileKeyString(key);
    const tile = this.#tiles.get(id);
    if (tile === undefined) return;
    if (tile.pixels !== null) {
      // 只有当没有快照还引用它时，内存才真的归还——由账本自动处理
      tile.pixels.release();
      tile.pixels = null;
    }
    tile.dirty = true;
    tile.state = 'resident';
  }

  /** 丢弃整个图层的所有块。删除图层时用。 */
  dropLayer(layer: LayerId): void {
    const ids = this.#byLayer.get(layer);
    if (ids === undefined) return;
    for (const id of ids) {
      this.#tiles.get(id)?.pixels?.release();
      this.#tiles.delete(id);
    }
    this.#byLayer.delete(layer);
  }

  /** 取图层句柄。图层不持有像素，只持有它。 */
  handle(layer: LayerId): TileHandle {
    let residentTiles = 0;
    for (const id of this.#byLayer.get(layer) ?? []) {
      if (this.#tiles.get(id)?.pixels != null) residentTiles++;
    }
    return {
      layer,
      residentTiles,
      allocatedBytes: residentTiles * BYTES_PER_TILE,
    };
  }

  /** 遍历某图层已分配的块。用生成器：调用方通常会提前短路。 */
  *residentTilesOf(layer: LayerId, lod?: LodLevel): Generator<Tile> {
    for (const id of this.#byLayer.get(layer) ?? []) {
      const tile = this.#tiles.get(id);
      if (tile?.pixels == null) continue;
      if (lod !== undefined && tile.key.lod !== lod) continue;
      yield toTile(tile);
    }
  }

  /** 标记/清除脏位。合成完成后由合成器清除（M1-05 会用到）。 */
  setDirty(key: TileKey, dirty: boolean): void {
    const tile = this.#tiles.get(tileKeyString(key));
    if (tile !== undefined) tile.dirty = dirty;
  }

  stats(): StoreStats {
    let residentTiles = 0;
    for (const tile of this.#tiles.values()) {
      if (tile.pixels !== null) residentTiles++;
    }
    return {
      residentTiles,
      trackedTiles: this.#tiles.size,
      allocatedBytes: this.#ledger.bytes,
      layers: this.#byLayer.size,
    };
  }

  /** 已分配字节数。读账本即可，无需遍历。 */
  get allocatedBytes(): number {
    return this.#ledger.bytes;
  }

  /* ---------------- 驱逐与回载（M1-03） ---------------- */

  /**
   * 驱逐一块：释放其内存，但**保留登记项**并标记为 `evicted`。
   *
   * 保留登记项而非删除，是为了让脏区传播与图层遍历不必区分"驱逐了"和"不存在"——
   * 两者本就该被同等对待，差别只在读取时要不要回载。
   *
   * 被快照引用的块驱逐了也不省内存（COW 里别人还持有），由调用方判断后跳过。
   */
  evict(key: TileKey): void {
    const tile = this.#tiles.get(tileKeyString(key));
    if (tile?.pixels == null) return;
    tile.pixels.release();
    tile.pixels = null;
    tile.state = 'evicted';
  }

  /** 回载完成，把数据放回内存。 */
  restoreEvicted(key: TileKey, bytes: Uint8Array): void {
    this.#store(key, SharedPixels.from(bytes, this.#ledger));
    const tile = this.#tiles.get(tileKeyString(key));
    if (tile !== undefined) tile.state = 'resident';
  }

  /** 块的驻留状态。 */
  stateOf(key: TileKey): TileState | null {
    return this.#tiles.get(tileKeyString(key))?.state ?? null;
  }

  /* ---------------- 写时复制（M1-02） ---------------- */

  /**
   * 对一组块拍快照。
   *
   * **不复制像素**，只把每块 retain 一次——成本与块数成正比、与像素量无关。
   * 这是撤销点能做到近乎零成本的全部原因（§4.5）。
   */
  snapshot(keys: Iterable<TileKey>): TileSnapshot {
    const entries = new Map<string, SharedPixels | null>();
    for (const key of keys) {
      const id = tileKeyString(key);
      if (entries.has(id)) continue; // 同一块传两次不该被 retain 两次
      const tile = this.#tiles.get(id);
      entries.set(id, tile?.pixels?.retain() ?? null);
    }
    return new TileSnapshot(entries);
  }

  /** 对某图层当前已分配的块整体拍快照。 */
  snapshotLayer(layer: LayerId): TileSnapshot {
    const keys: TileKey[] = [];
    for (const id of this.#byLayer.get(layer) ?? []) {
      const tile = this.#tiles.get(id);
      if (tile?.pixels != null) keys.push(tile.key);
    }
    return this.snapshot(keys);
  }

  /**
   * 从快照恢复一组块。撤销就是这一步。
   *
   * 恢复同样不复制：把快照里的像素 retain 一次挂回当前状态即可。
   */
  restore(snapshot: TileSnapshot, keys: Iterable<TileKey>): void {
    for (const key of keys) {
      const bytes = snapshot.get(key);
      if (bytes === null) this.clear(key);
      else this.set(key, bytes);
    }
  }

  /**
   * 取得一块的写入许可。
   *
   * 会在必要时完成写时复制（该块被快照共享时先克隆），因此 guard 里的
   * `bytes` 一定是**独占**的，就地写入不会影响任何快照。
   *
   * 同一块同时只能有一个未释放的 guard，重复获取抛 {@link ConcurrentWriteError}——
   * 见 cow.ts 里关于"并发写"的说明。
   */
  beginWrite(key: TileKey): WriteGuard {
    const id = tileKeyString(key);
    if (this.#writing.has(id)) throw new ConcurrentWriteError(key);

    const bytes = this.allocate(key); // 内部已处理 COW 克隆
    this.#writing.add(id);

    const tile = this.#tiles.get(id);
    if (tile !== undefined) tile.dirty = true;

    return new WriteGuard(key, bytes, () => {
      this.#writing.delete(id);
    });
  }

  /** 是否有未释放的写入许可。 */
  isWriting(key: TileKey): boolean {
    return this.#writing.has(tileKeyString(key));
  }

  #indexLayer(layer: LayerId, id: string): void {
    let set = this.#byLayer.get(layer);
    if (set === undefined) {
      set = new Set();
      this.#byLayer.set(layer, set);
    }
    set.add(id);
  }
}
